import { Suspense } from "react";
import { useTranslations } from "use-intl";
import { Skeleton } from "@/components/ui/skeleton";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierTermsAndConditionsRecord } from "@/app/Suppliers/GraphQL/supplierTermsAndConditionsRecord";
import { RelocationType } from "@/gql/graphql";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useBookingRecord } from "@/app/Bookings/Hooks/useBookingRecord";

import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { Separator } from "@/components/ui/separator";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { SupplierInsuranceOptionEditDialog } from "@/app/Suppliers/Components/SupplierInsuranceOptionEditDialog";
import { BookingChangeInsuranceDialog } from "@/app/Bookings/Components/Dialogs/BookingChangeInsuranceDialog";
import { RawText } from "@/lib/Components/RawText";
import { Button } from "@/components/catalyst/button";

export function BookingInsuranceCard() {
  const t = useTranslations("booking.cards.insuranceCard");
  const booking = useBookingRecord();
  const { open } = useDialog(SupplierInsuranceOptionEditDialog);
  const { open: changeInsurance } = useDialog(BookingChangeInsuranceDialog);

  const insuranceOption = booking.supplierInsuranceOption;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-muted/50 flex flex-row items-center">
        <div className="grid gap-0.5">
          <CardTitle className="group flex items-center gap-2 text-lg">
            {t("title")}
          </CardTitle>
          <CardDescription>
            {booking.supplierInsuranceOption?.name}
          </CardDescription>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            outline
            onClick={() => {
              open({ insuranceOptionId: insuranceOption.id });
            }}
          >
            {t("editInsurance")}
          </Button>
          <Button
            outline
            onClick={() => {
              changeInsurance({ bookingId: booking.id });
            }}
          >
            {t("changeInsurance")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6 text-sm">
        <div className="grid gap-3">
          <div className="font-semibold">{t("detailsTitle")}</div>
          <dl className="grid gap-3">
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{t("bond")}</dt>
              <dd>
                {formatCurrency(insuranceOption?.bond_amount, booking.currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{t("standardLiability")}</dt>
              <dd>
                {formatCurrency(
                  insuranceOption?.standard_liability_amount,
                  booking.currency,
                )}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{t("creditCardAccepted")}</dt>
              <dd>✅</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">{t("debitCardAccepted")}</dt>
              <dd>{booking.supplier.debit_card_accepted ? "✅" : "❌"}</dd>
            </div>
          </dl>
        </div>
        <Separator className="my-4" />
        <div className="grid gap-3">
          <div className="font-semibold">{t("reductionOptionsTitle")}</div>
          <RawText>{insuranceOption.liability_reduction_options}</RawText>
        </div>

        <Separator className="my-4" />

        <div className="grid gap-3">
          <div className="font-semibold">{t("termsTitle")}</div>
          <Suspense fallback={<Skeleton className="h-96 w-full" />}>
            <BookingTermsPreview
              supplierId={booking.supplier.id}
              relocationType={booking.relocation.type}
            />
          </Suspense>
        </div>
      </CardContent>
    </Card>
  );
}

function BookingTermsPreview({
  supplierId,
  relocationType,
}: {
  supplierId: string;
  relocationType: RelocationType;
}) {
  const { data } = useSuspenseGqlQuery(supplierTermsAndConditionsRecord, {
    supplierId,
    relocationType,
  });
  const t = useTranslations("booking.cards.insuranceCard");
  const pdfUrl = data.supplierTermsAndConditions?.pdf_url;

  return pdfUrl ? (
    <iframe
      src={pdfUrl}
      title={t("termsTitle")}
      className="h-[600px] w-full rounded-md border border-gray-200"
    />
  ) : (
    <p className="text-gray-500">{t("noTerms")}</p>
  );
}
